import { useCallback, useState } from 'react';
import { OverflowMenu } from '@stitch-fix/mode-react';
import { datadogRum } from '@datadog/browser-rum';
import type { ReportSelectEventData } from '@stitch-fix/event-reporter';
import { useSelectEvent } from '@stitch-fix/event-reporter';
import { COPY } from '../../utils/copy';
import { RemoveItemModal } from '../RemoveItemModal/RemoveItemModal';
import FeedbackModal from '../FeedbackModal';
import { useBoughtItemsMachine } from '../../utils/machines/boughtItems/context-provider';
import { useToast } from '../../utils/contexts/toast-provider';
import { useWardrobeGotoUpdateMutation, WardrobeGotoUpdateMutation } from '../../utils/hooks/updateTopItem';
import { formatQueryVariables } from './BoughtItemsLayout';
import { BoughtItemsBoughtItemFragment, GetBoughtItemsDocument } from '../../utils/hooks/getBoughtItems';
import { GetTopItemsDocument } from '../../utils/hooks/getTopItems';
import { CancelReturnModal } from '../CancelReturnModal/CancelReturnModal';
import { useWardrobeParams, WardrobeParams } from '../../utils/hooks/useWardrobeParams';
type BoughtItemsOverflowMenuProps = {
  boughtItem: BoughtItemsBoughtItemFragment;
};
const leaveFeedbackOption = {
  id: 'leave-feedback',
  label: 'Leave feedback'
};
const makeTopItemOption = {
  id: 'update-top-item',
  label: 'Make a top item'
};
const removeFromTopItemsOption = {
  id: 'update-top-item',
  label: 'Remove from top items',
  variant: 'destructive'
};
const removeFromBoughtOption = {
  id: 'remove-from-bought',
  label: 'Remove from bought',
  variant: 'destructive'
};
const cancelReturn = {
  id: 'cancel-return',
  label: 'Cancel return',
  variant: 'destructive'
};

/**
 * Event object to report select event for remove bought item modal
 */
const removeItemModalSelectEvent: ReportSelectEventData = {
  schema: 'select',
  name: 'remove_from_bought',
  action_name: 'view_remove_kept_item_modal'
};

/**
 * Handles the logic for which options to show in the overflow menu
 */
const overflowMenuItems = (isGoToItem: boolean, returnInProgress: boolean) => {
  if (isGoToItem && returnInProgress) {
    return [cancelReturn, removeFromTopItemsOption];
  }
  if (isGoToItem && !returnInProgress) {
    return [leaveFeedbackOption, removeFromTopItemsOption, removeFromBoughtOption];
  }
  if (!isGoToItem && returnInProgress) {
    return [cancelReturn, makeTopItemOption];
  }
  return [makeTopItemOption, leaveFeedbackOption, removeFromBoughtOption];
};

/**
 * The `BoughtItemsOverflowMenu` component handles rendering a list of actions
 * that a user can choose from a temporary popup overlay.
 */

export const BoughtItemsOverflowMenu = ({
  boughtItem
}: BoughtItemsOverflowMenuProps) => {
  const {
    wardrobeParams
  } = useWardrobeParams();
  const {
    send
  } = useBoughtItemsMachine();
  const [removeItemModalOpened, setRemoveItemModalOpened] = useState<boolean>(false);
  const [feedbackModalOpened, setFeedbackModalOpened] = useState<boolean>(false);
  const [cancelReturnModalOpened, setCancelReturnModalOpened] = useState<boolean>(false);
  const {
    addToast
  } = useToast();
  const {
    errorToastMessage
  } = COPY;
  const {
    reportSelectEvent
  } = useSelectEvent();
  const handleRemoveItemModalOnClose = () => {
    setRemoveItemModalOpened(false);
  };
  const handleFeedbackModalClose = () => {
    setFeedbackModalOpened(false);
  };
  const handleCancelReturnModalClose = () => {
    setCancelReturnModalOpened(false);
  };
  const [updateTopItem] = useWardrobeGotoUpdateMutation({
    variables: {
      skuId: boughtItem.sku
    },
    onCompleted: ({
      wardrobeGotoUpdate
    }: WardrobeGotoUpdateMutation) => {
      const result = wardrobeGotoUpdate?.result;

      /**
       * If a user tries to add more than 10 items to their top items, we want
       * to show a toast telling them they cannot, but not update the UI.
       */
      if (result && result.__typename === 'WardrobeGotoUpdateError') {
        addToast({
          message: result.reason,
          status: 'error'
        });
      }
      /**
       * When a user successfully adds an item to their top items, we want to display
       * a toast, as well as send('UPDATE') to the state machine to update the UI.
       */
      if (result && result.__typename === 'WardrobeGotoUpdateSuccess') {
        addToast({
          message: result.successMessage,
          status: 'success'
        });
        if (result.successMessage === 'Removed from Top Items') {
          send('REMOVE_TOP_ITEM', {
            removedSkuId: boughtItem.sku
          });
        } else {
          send('ADD_TOP_ITEM', {
            addedSkuId: boughtItem.sku
          });
        }
      }
    },
    onError: e => {
      addToast({
        message: errorToastMessage
      });
      datadogRum.addError(e, {
        query: 'useWardrobeGotoUpdateMutation',
        component: 'BoughtItemsOverflowMenu'
      });
    },
    refetchQueries: [{
      query: GetTopItemsDocument
    }, {
      query: GetBoughtItemsDocument,
      variables: formatQueryVariables(wardrobeParams as WardrobeParams)
    }]
  });
  const handleUpdateTopItem = useCallback(() => {
    updateTopItem();
  }, [updateTopItem]);
  const handleSelect = useCallback((selection: string) => {
    switch (selection) {
      case 'update-top-item':
        return handleUpdateTopItem();
      case 'leave-feedback':
        return setFeedbackModalOpened(true);
      case 'remove-from-bought':
        reportSelectEvent(removeItemModalSelectEvent);
        return setRemoveItemModalOpened(true);
      case 'cancel-return':
        return setCancelReturnModalOpened(true);
      default:
        return null;
    }
  }, [handleUpdateTopItem, reportSelectEvent]);
  return <>
      <OverflowMenu aria-label="See more options" items={overflowMenuItems(boughtItem.variant.isGoToItem, boughtItem.returnInProgress)} onSelect={handleSelect} iconButtonVariant="overlay" />
      <RemoveItemModal open={removeItemModalOpened} handleModalClose={handleRemoveItemModalOnClose} skuId={boughtItem.sku} orderId={boughtItem.orderId} context="wardrobe_bought_items" />
      <FeedbackModal open={feedbackModalOpened} handleModalClose={handleFeedbackModalClose} skuId={boughtItem.sku} orderId={boughtItem.orderId} returnInProgress={boughtItem.returnInProgress} />
      <CancelReturnModal open={cancelReturnModalOpened} handleModalClose={handleCancelReturnModalClose} skuId={boughtItem.sku} orderId={boughtItem.orderId} />
    </>;
};